import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { IconButton } from '@/components/console-ui';
import type { LogoBarsState } from './LogoBars';
import { LogoBars } from './LogoBars';

type ChatPanelFrameProps = {
  /** The landmark's name, e.g. "AI editor". */
  label: string;
  header: ReactNode;
  log: ReactNode;
  footer: ReactNode;
  /** A data attribute the caller finds its panel by, set empty, e.g. `data-ai-panel`. */
  markerAttribute?: `data-${string}`;
  /**
   * The frame's edge: a left border where the panel is a column beside other content (the editor),
   * the console panel's rounded border where it stands on its own (the dashboard).
   */
  edge?: string;
};

/**
 * An AI chat panel (handoff 4.7): a 2px line draws across the top as it opens and the header, log
 * and input rise in sequence; under reduced motion it all appears at once (ai-chat.css). The
 * caller moves focus to its input on open.
 */
export const ChatPanelFrame = ({
  label,
  header,
  log,
  footer,
  markerAttribute,
  edge = 'border-l',
}: ChatPanelFrameProps) => (
  <aside
    {...(markerAttribute ? { [markerAttribute]: '' } : {})}
    aria-label={label}
    className={`relative flex h-full min-w-0 flex-col overflow-hidden ${edge} border-line bg-panel`}
  >
    <span aria-hidden="true" className="ai-panel-line absolute inset-x-0 top-0 h-0.5 bg-foreground" />
    <div className="ai-panel-rise" style={{ animationDelay: '120ms' }}>
      {header}
    </div>
    <div className="ai-panel-rise flex min-h-0 flex-1 flex-col" style={{ animationDelay: '200ms' }}>
      {log}
    </div>
    <div className="ai-panel-rise" style={{ animationDelay: '360ms' }}>
      {footer}
    </div>
  </aside>
);

type ChatPanelHeaderProps = {
  title: string;
  /** The bars' motion: they move with the turn. */
  state: LogoBarsState;
  /** Beside the title, e.g. the editor's scope menu. */
  children?: ReactNode;
  closeLabel: string;
  onClose: () => void;
};

/** The panel's header: the bars, the title, whatever the feature puts beside it, and close. */
export const ChatPanelHeader = ({ title, state, children, closeLabel, onClose }: ChatPanelHeaderProps) => (
  <div className="flex shrink-0 items-center gap-2 border-b border-line py-3 pr-2.5 pl-4">
    <LogoBars size={14} state={state} />
    <h2 className="text-body font-semibold text-foreground">{title}</h2>
    {children}
    <IconButton label={closeLabel} icon={<X />} size={28} tooltipSide="bottom" onClick={onClose} className="ml-auto" />
  </div>
);

/**
 * The conversation: a `role="log"` landmark that is itself silent (`aria-live="off"`), because a
 * finished turn would otherwise be read out whole as it appears. Each feature says what a turn did
 * once, in its own live region.
 */
export const ChatLogFrame = ({ children }: { children: ReactNode }) => (
  <div
    role="log"
    aria-live="off"
    aria-label="Conversation"
    className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-4 py-[18px]"
  >
    {children}
  </div>
);
